import debounce from 'lodash/debounce';
import Tagify from '@yaireo/tagify';

class Hashtag {
  constructor() {
    const hashtag_input = document.querySelector('.js-hashtag');
    if (!hashtag_input) return;

    let isCompositionStart = false;

    const tagify = new Tagify(hashtag_input, {
      delimiters: ',| ',
      trim: false,
      backspace: false,
      addTagOnBlur: false,
      dropdown: {
        position: 'input',
        classname: 'suggest-hashlist',
        maxItems: -1,
        enabled: 0,
      },
      maxTags: hashtag_input.dataset.maxCount,
      placeholder: '#',
      originalInputValueFormat: (values) =>
        values.map((item) => item.value).join(','),
      templates: {
        wrapper: this.wrapperTemplate,
        tag: this.tagTemplate,
      },
    });

    const allowlist_tag = document.querySelector(
      hashtag_input.dataset.allowlist,
    );
    if (allowlist_tag) {
      tagify.settings.whitelist = allowlist_tag.value.split(',');
    }
    this.$current_tag_count = document.querySelector('.js_tag_count');
    this.showRemainCount(tagify);

    // onKeyDownイベントに含まれるIMEバグに対するOverride
    tagify.dropdown.events.callbacks.onKeyDown = this.onKeyDown.bind(tagify);

    // IME用文字列変換開始
    tagify.DOM.input.addEventListener('compositionstart', (e) => {
      isCompositionStart = true;
    });

    // IME用文字列変換完了
    tagify.DOM.input.addEventListener('compositionend', (e) => {
      isCompositionStart = false;
    });

    tagify
      .on('remove', () => {
        isCompositionStart = false;
        this.showRemainCount(tagify);
      })
      .on('add', () => {
        isCompositionStart = false;
        this.showRemainCount(tagify);
      })
      .on(
        'input',
        debounce(async (e) => {
          if (!e.detail.value) {
            return false;
          }
          // FirefoxでもIEでもなく、且つ半角英数の時にはトリガーしない
          if (
            !tagify.isFirefox &&
            !tagify.isIE &&
            (!e.detail.value.match(/^[A-Za-z0-9]*$/) || isCompositionStart)
          ) {
            return false;
          }
        }, 200),
      );
  }

  showRemainCount(tagify) {
    this.$current_tag_count.setAttribute(
      'data-count',
      '(あと' + (tagify.settings.maxTags - tagify.value.length) + '個)',
    );
    if (tagify.value.length > 0) {
      this.$current_tag_count.style.display = 'block';
    } else {
      this.$current_tag_count.style.display = 'none';
    }
  }

  // Tagify.tagの一番外側のタグ部分の変更
  // https://github.com/yairEO/tagify/blob/v4.13.3/src/parts/templates.js#L7-L21
  wrapperTemplate(input, _s) {
    return `<tags class="${_s.classNames.namespace} ${_s.mode ? `${_s.classNames[_s.mode + 'Mode']}` : ''} ${input.className}"
                ${_s.readonly ? 'readonly' : ''}
                ${_s.disabled ? 'disabled' : ''}
                ${_s.required ? 'required' : ''}
                ${_s.mode === 'select' ? "spellcheck='false'" : ''}
                tabIndex="-1">
              &#8203;
              <span class="append_tag_message"></span>
              <div class="current_tag_count js_tag_count"></div>
              <span
                ${!_s.readonly && _s.userInput ? 'contenteditable' : ''} 
                tabIndex="0" data-placeholder="${_s.placeholder || '&#8203;'}" aria-placeholder="${_s.placeholder || ''}"
                class="${_s.classNames.input} dropdown-append-target"
                role="textbox"
                aria-autocomplete="both"
                aria-multiline="${_s.mode == 'mix' ? true : false}"></span>
            </tags>`;
  }

  // Tagifyの.tagの各タグ部分の変更
  // https://github.com/yairEO/tagify/blob/v4.13.3/src/parts/templates.js#L23-L35
  tagTemplate(tagData, tagify) {
    return `
      <tag
        title="${tagData.title || tagData.value}"
        contenteditable='false'
        spellcheck='false'
        tabIndex="${this.settings.a11y.focusableTags ? 0 : -1}"
        class="${this.settings.classNames.tag} ${tagData.class ? tagData.class : ''}"
        ${this.getAttributes(tagData)}>
          <span class="${this.settings.classNames.tagText}">${tagData[this.settings.tagTextProp] || tagData.value}</span>
          <x title='' class="${this.settings.classNames.tagX}" role='button' aria-label='remove tag'></x>
      </tag>`;
  }

  // TagifyのIMEバグ修正Monkeypatch
  // Enter時に229だった場合、処理から離脱するように修正
  // https://github.com/yairEO/tagify/blob/v4.13.3/dist/tagify.js
  // https://github.com/yairEO/tagify/blob/master/src/parts/dropdown.js#L389-L474
  onKeyDown(e) {
    // ignore keys during IME composition
    if (!this.state.hasFocus || this.state.composing) return;

    // get the "active" element, and if there was none (yet) active, use first child
    var selectedElm = this.DOM.dropdown.querySelector(
        this.settings.classNames.dropdownItemActiveSelector,
      ),
      selectedElmData = this.dropdown.getSuggestionDataByNode(selectedElm);

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Down': // >IE11
      case 'Up': {
        // >IE11
        e.preventDefault();
        var dropdownItems = this.dropdown.getAllSuggestionsRefs(),
          actionUp = e.key == 'ArrowUp' || e.key == 'Up';

        if (selectedElm) {
          selectedElm = this.dropdown.getNextOrPrevOption(
            selectedElm,
            !actionUp,
          );
        }

        // if no element was found OR current item is not a "real" item, loop
        if (
          !selectedElm ||
          !selectedElm.matches(this.settings.classNames.dropdownItemSelector)
        ) {
          selectedElm = dropdownItems[actionUp ? dropdownItems.length - 1 : 0];
        }

        this.dropdown.highlightOption(selectedElm, true);
        selectedElm.scrollIntoView({
          inline: 'nearest',
          block: 'center',
          behavior: 'smooth',
        });
        break;
      }
      case 'Escape':
      case 'Esc': // IE11
        this.dropdown.hide();
        break;

      case 'ArrowRight':
        if (this.state.actions.ArrowLeft) return;
      case 'Tab': {
        // in mix-mode, treat arrowRight like Enter key, so a tag will be created
        if (
          this.settings.mode != 'mix' &&
          selectedElm &&
          !this.settings.autoComplete.rightKey &&
          !this.state.editing
        ) {
          e.preventDefault(); // prevents blur so the autocomplete suggestion will not become a tag
          var value = this.dropdown.getMappedValue(selectedElmData);

          this.input.autocomplete.set.call(this, value);
          return false;
        }
        return true;
      }
      case 'Enter': {
        // Monkeypatch-Start
        if (e.keyCode === 229) return;
        // Monkeypatch-End
        e.preventDefault();

        this.settings.hooks
          .suggestionClick(e, {
            tagify: this,
            tagData: selectedElmData,
            suggestionElm: selectedElm,
          })
          .then(() => {
            if (selectedElm) {
              this.dropdown.selectOption(selectedElm);
              // highlight next option
              selectedElm = this.dropdown.getNextOrPrevOption(
                selectedElm,
                !actionUp,
              );
              this.dropdown.highlightOption(selectedElm);
              return;
            } else this.dropdown.hide();

            if (this.settings.mode != 'mix')
              this.addTags(this.state.inputText.trim(), true);
          })
          .catch((err) => err);

        break;
      }
    }
  }
}

export { Hashtag };
